import { useLayoutEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { useGetLMSAccountsDataQuery } from '@/api/endpoints/lmsData';
import type { ISchoolMainData } from '@/api/endpoints/schools';
import { useUpdateSchoolDataMutation } from '@/api/endpoints/schools';
import { DataSection, InputField, SelectField } from '@/shared/components';
import { ROUTES } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';
import { ESchoolBadgeIcons } from '@/shared/types';

import { IconSelectLabel } from '../_components/IconSelectLabel/IconSelectLabel';
import { CORPORATE_SCHOOL_VALIDATION_SCHEMA } from '../consts';

import styles from './SchoolEditPage.module.scss';
import type { TCorporateSchoolFormData } from './types';

interface IProps {
  schoolData: ISchoolMainData;
}

export const EditCorporateSchoolComponent = ({ schoolData }: IProps) => {
  const sendApiRequest = useApiRequest();

  const navigate = useNavigate();

  const { data: lmsAccountsData = [], isLoading: areLMSAccountsLoading } = useGetLMSAccountsDataQuery();

  const [updateSchool, { isLoading: isSchoolUpdating }] = useUpdateSchoolDataMutation();

  const { name, icon, lms_account_id: initialLMSAccountId, active: isActive, id: schoolId } = schoolData;

  const methods = useForm<TCorporateSchoolFormData>({
    defaultValues: {
      title: name ?? '',
      icon: icon ?? '',
      lmsAccountId: initialLMSAccountId ?? null,
    },
    resolver: yupResolver(CORPORATE_SCHOOL_VALIDATION_SCHEMA),
  });

  const { handleSubmit } = methods;

  const badgeIconsList = useMemo(
    () =>
      Object.values(ESchoolBadgeIcons).map((iconType) => ({
        value: iconType,
        label: <IconSelectLabel iconType={iconType} />,
      })),
    [],
  );
  const lmsAccountsList = useMemo(
    () => lmsAccountsData.map(({ id, name }) => ({ value: id, label: name })),
    [lmsAccountsData],
  );

  const goBack = () => {
    navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}`);
  };

  const publishSchool = () =>
    sendApiRequest({
      successMessage: 'Школа опубликована',
      successCallback: () => void navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}`),
      apiRequestCallback: () =>
        updateSchool({
          id: Number(schoolId),
          data: {
            active: true,
          },
        }),
    });

  const saveSchoolData = handleSubmit(({ title, icon, lmsAccountId }) => {
    if (!lmsAccountId) {
      return;
    }

    const data = {
      name: title,
      icon,
      lms_account_id: lmsAccountId,
    };

    sendApiRequest({
      successMessage: 'Школа успешно обновлена',
      apiRequestCallback: () =>
        updateSchool({
          id: schoolId,
          data,
        }),
    });
  });

  useLayoutEffect(() => {
    if (isActive) {
      navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${schoolId}/${ROUTES.VIEW}`);
    }
  }, [isActive, schoolId]);

  return (
    <div className={styles['content-wrapper-fullheight']}>
      <FormProvider {...methods}>
        <DataSection
          titleText="Название"
          isTitleRequired
        >
          <InputField
            fieldName="title"
            placeholder="Название"
          />
        </DataSection>
        <DataSection
          titleText="Аккаунт школы в LMS"
          isTitleRequired
        >
          <SelectField
            fieldName="lmsAccountId"
            size="large"
            placeholder="Выбрать аккаунт"
            options={lmsAccountsList}
            showSearch={{
              optionFilterProp: 'label',
            }}
            loading={areLMSAccountsLoading}
            disabled={areLMSAccountsLoading}
          />
        </DataSection>
        <DataSection titleText="Иконка школы">
          <SelectField
            fieldName="icon"
            size="large"
            virtual={false}
            showSearch={false}
            placeholder="Выбрать иконку"
            options={badgeIconsList}
          />
        </DataSection>
        <div className={styles['buttons-wrapper']}>
          <Button
            onClick={goBack}
            type="dashed"
            size="large"
            className={styles.button}
          >
            Назад
          </Button>
          <div style={{ display: 'flex', gap: '16px' }}>
            <Button
              onClick={saveSchoolData}
              type="default"
              size="large"
              className={styles['save-button']}
              disabled={isSchoolUpdating}
            >
              Сохранить
            </Button>
            <Button
              onClick={publishSchool}
              type="primary"
              size="large"
              className={styles['publish-button']}
              disabled={isSchoolUpdating}
            >
              Опубликовать
            </Button>
          </div>
        </div>
      </FormProvider>
    </div>
  );
};
